import { useCallback, useEffect, useRef } from 'react';
import _ from 'lodash';
import { RxNoodleDBCollectionsKeys } from '../rxdb.types';
import {
  getQuestionnaireFieldTemplateReplicationIdentifier,
  getQuestionnaireTemplateReplicationIdentifier,
  prioritizeReplications,
} from '../rxdb.replication';

type CollectionIds = undefined | null | string | string[];
type Collections<T> = Partial<Record<RxNoodleDBCollectionsKeys, T>>;

const usePrioritizeReplication = (collections: Collections<CollectionIds>): void => {
  const prioritizedReplications = useRef<Collections<string[]>>({});

  const addToPrioritizedReplications = useCallback((collectionName: RxNoodleDBCollectionsKeys, collectionIds: string[]): void => {
    prioritizedReplications.current = {
      ...prioritizedReplications.current,
      [collectionName]: Array.from(new Set([...(prioritizedReplications.current?.[collectionName] ?? []), ...collectionIds])),
    };
  }, []);

  const ensureReplicationsArePrioritized = useCallback((): void => {
    const addedIdentifiers = new Set<string>();
    const entries = Object.entries(collections) as [RxNoodleDBCollectionsKeys, CollectionIds][];

    entries.forEach(([collectionName, collectionIds]) => {
      const existingIdentifiers = prioritizedReplications.current?.[collectionName];
      const arrayCollectionIds = (Array.isArray(collectionIds) ? collectionIds : [collectionIds]).filter(Boolean) as string[];

      if (existingIdentifiers && _.isEqual(existingIdentifiers, arrayCollectionIds)) {
        return;
      }

      addToPrioritizedReplications(collectionName, arrayCollectionIds);

      if (collectionName === 'questionnaireTemplate') {
        const identifier = getQuestionnaireTemplateReplicationIdentifier();
        addedIdentifiers.add(identifier);
        return;
      }

      if (collectionName === 'questionnaireFieldTemplate') {
        arrayCollectionIds.forEach(id => {
          const existingId = existingIdentifiers?.includes(id);
          if (existingId) return;

          const identifier = getQuestionnaireFieldTemplateReplicationIdentifier(id);
          addedIdentifiers.add(identifier);
        });
      }
    });

    const identifiers = Array.from(addedIdentifiers);
    if (identifiers.length > 0) prioritizeReplications(identifiers);
  }, [collections, addToPrioritizedReplications]);

  useEffect(() => {
    ensureReplicationsArePrioritized();
  }, [ensureReplicationsArePrioritized]);
};

export default usePrioritizeReplication;
